import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Map from '@planet/maps/Map';
import Overlay from '@planet/maps/Overlay';
import View from '@planet/maps/View';
import LinkInteraction from '@planet/maps/interaction/Link';
import MapboxLayer from '@planet/maps/layer/MapboxVector';
import VectorLayer from '@planet/maps/layer/Vector';
import VectorSource from '@planet/maps/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import WKT from 'ol/format/WKT';
import {fromExtent} from 'ol/geom/Polygon';
import {useGeographic as geographic} from 'ol/proj';
import {array, func, string} from 'prop-types';
import React, {useCallback, useRef, useState} from 'react';
import {defaultFeatureStyle, highlightFeatureStyle} from '../theme.js';
import Link from './Link.jsx';

const geojsonFormat = new GeoJSON();
const wktFormat = new WKT();

geographic();
const accessToken = import.meta.env.VITE_MAPBOX_TOKEN;
const styleUrl = 'mapbox://styles/mapbox/light-v10';

function getViewExtentWKT(view) {
  const extent = view.calculateExtent();
  const geometry = fromExtent(extent);
  return wktFormat.writeGeometry(geometry);
}

function PublicationsMap({publications, highlight, onChange}) {
  const [hover, setHover] = useState(null);
  const viewRef = useRef(null);
  const layerRef = useRef(null);
  const [popupPosition, setPopupPosition] = useState(null);
  const [popupElement, setPopupElement] = useState(null);
  const [selectedPublication, setSelectedPublication] = useState(null);

  const features = publications
    .filter(p => p.geometry)
    .map(p =>
      geojsonFormat.readFeature({
        type: 'Feature',
        id: p.id,
        geometry: p.geometry,
        properties: {
          title: p.title,
        },
      }),
    );

  const featureStyle = useCallback(
    feature => {
      const id = highlight || hover;
      if (!id || feature.getId() !== id) {
        return defaultFeatureStyle;
      }
      return highlightFeatureStyle;
    },
    [highlight, hover],
  );

  const onMapMoveEnd = useCallback(() => {
    const view = viewRef.current;
    const center = view.getCenter();
    const lon = center[0];
    if (lon > 180 || lon < -180) {
      view.setCenter([((lon + 180) % 360) - 180, center[1]]);
      return;
    }
    onChange(getViewExtentWKT(view));
  }, [onChange]);

  const layerFilter = useCallback(layer => layer === layerRef.current, []);

  const onPointerMove = useCallback(
    event => {
      const map = event.target;
      const features = map.getFeaturesAtPixel(event.pixel, {layerFilter});
      const viewport = map.getViewport();
      if (features.length === 0) {
        viewport.style.cursor = 'inherit';
        setHover(null);
        return;
      }
      viewport.style.cursor = 'pointer';
      setHover(features[0].getId());
    },
    [layerFilter],
  );

  const onClick = useCallback(
    event => {
      const map = event.target;
      const features = map.getFeaturesAtPixel(event.pixel, {layerFilter});
      if (!features.length) {
        setPopupPosition(null);
        return;
      }
      const feature = features[0];
      setSelectedPublication({
        id: feature.getId(),
        title: feature.get('title'),
      });
      setPopupPosition(event.coordinate);
    },
    [layerFilter],
  );

  return (
    <Box sx={{height: '100%', width: '100%', position: 'relative'}}>
      <Map
        onMoveEnd={onMapMoveEnd}
        onPointerMove={onPointerMove}
        onClick={onClick}
      >
        <View options={{center: [0, 0], zoom: 2}} ref={viewRef} />
        <LinkInteraction />
        <MapboxLayer options={{accessToken, styleUrl}} />
        <VectorLayer
          options={{className: 'render-over-labels'}}
          style={featureStyle}
          ref={layerRef}
        >
          <VectorSource features={features} />
        </VectorLayer>
        <Overlay
          options={{
            autoPan: {
              animation: {duration: 500},
            },
            positioning: 'bottom-center',
          }}
          position={popupPosition}
          element={popupElement}
        />
      </Map>

      <Paper
        ref={setPopupElement}
        sx={{
          width: '250px',
          padding: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {selectedPublication && (
          <>
            <Typography paragraph variant="caption" sx={{marginBottom: 0}}>
              {selectedPublication.title}
            </Typography>
            <Button
              size="small"
              component={Link}
              to={`/publications/${selectedPublication.id}`}
              sx={{marginLeft: 'auto'}}
            >
              View
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}

PublicationsMap.propTypes = {
  publications: array.isRequired,
  highlight: string,
  onChange: func.isRequired,
};

export default PublicationsMap;
